import React, { useState, useLayoutEffect } from "react"
import JamPreview from "./JamPreview"
import humanReadableTimeFrom from "../util/humanReadableTimeAgo"
import resolveThreadById from "../util/fetchApiHelpers/resolveThreadById"
import JamThreadEditor from "./JamThreadEditor"
import JamEditorDisplayMode from "./JamEditorDisplayMode"
import positionCommentX from "../util/positionCommentX"
import positionCommentY from "../util/positionCommentY"
import isALoomVideo from "../util/isALoomVideo"
import type { Mention, Comment, Thread } from "../types"
import useCurrentUrl from "../hooks/useCurrentUrl"

function getEmbeddableVideo(
	messageBlock: Draft.RawDraftContentState,
): string | null {
	let embedUrl = null

	messageBlock.blocks.forEach((block) => {
		const message = block.text
		if (
			message.includes("loom") &&
			(message.includes("share") || message.includes("embed")) &&
			isALoomVideo(message) !== null
		) {
			embedUrl = isALoomVideo(message)
		}
	})

	return embedUrl
}

export interface JamThreadProps {
	JAM_ID: string

	thread: Thread
	detachedThread: Thread
	mentions: Mention[]

	socket: SocketIOClient.Socket

	typeOfDetachedThread: "resolved" | "detached"
	threadOpen: boolean
	threadIndex: number
	onSelectThread: (threadIndex: number) => void

	isShowingNotifications: boolean

	onCommentChange: (comment: Comment) => void

	onThreadChange: (thread: Thread) => void
	onThreadResolve: (threadId: string) => void

	loading: boolean
	onLoadingChange: (loadingStatus: boolean) => void
}

export default function JamThread(props: JamThreadProps) {
	let { detachedThread, thread, threadOpen } = props

	function handlePreviewClick(event: React.MouseEvent<HTMLElement>) {
		// when one thread opens, other threads must close
		if (
			!props.isShowingNotifications &&
			(event.target as HTMLElement).id !== "eye-icon-toggle-preview"
		) {
			props.onSelectThread(!threadOpen ? props.threadIndex : -1)
		}
	}

	let [resolveLoading, setResolveLoading] = useState(false)

	async function handleResolveClick() {
		// start loading
		setResolveLoading(true)
		await resolveThreadById(thread._id, props.JAM_ID)
		props.onThreadResolve(thread._id)
		props.socket.emit("threadResolved", thread._id)
		// stop loading
		setResolveLoading(false)
	}

	let selector = thread.location.selector
	let currentUrl = useCurrentUrl()

	let [isTargetElementVisible, setIsTargetElementVisible] = useState(false)

	useLayoutEffect(() => {
		let element = document.querySelector(selector)
		if (element) {
			let computedStyle = window.getComputedStyle(element)
			let isVisible = computedStyle.display !== "none"
			setIsTargetElementVisible(isVisible)
		}
		// We want
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selector, currentUrl])

	// TODO: This should all be refactored to use a ref
	let [positionedX, setPositionedX] = useState(0)
	let [positionedY, setPositionedY] = useState(0)
	useLayoutEffect(() => {
		if (detachedThread || !isTargetElementVisible || !threadOpen) {
			setPositionedX(0)
			setPositionedY(0)
		} else {
			setPositionedX(positionCommentX(thread, "thread"))
			setPositionedY(positionCommentY(thread, "thread"))
		}
	}, [detachedThread, isTargetElementVisible, thread, threadOpen])

	if (!isTargetElementVisible) {
		return null
	}

	if (!threadOpen) {
		return <JamPreview onClick={handlePreviewClick} thread={thread} />
	}

	return (
		<div
			id={`jam--thread-container-${thread._id}`}
			className={`jam--thread-container ${thread._id}`}
			style={{ top: `${positionedY}px`, left: `${positionedX}px` }}
		>
			{/* if thread is being displayed in a detached way leave a little note */}
			{props.detachedThread && (
				<div className="jam--thread-infobox">
					{props.typeOfDetachedThread === "detached"
						? `This comment thread was left on an element that is not currently displayed on the page: ${thread.location.selector}`
						: "This comment thread was already resolved."}
				</div>
			)}
			<div className={`jam--thread-header-container ${thread._id}`}>
				<div style={{ display: "flex" }}>
					<img
						className="jam--thread-header-author-avatar"
						src={thread.author.avatar}
					/>
					<div className="jam--thread-header-author-name">
						{thread.author.name}
					</div>
					<div className="jam--thread-header-created-at">
						{humanReadableTimeFrom(thread._id)}
					</div>
					<style>{`
						.jam--thread-infobox {
							margin: 1em;
							padding: 0.5em;
							background: #fff5e4;
							border: 1px solid #ffbc4c;
							color: #cc8f29;
							font-weight: bold;
							line-height: 20px;
							font-family: "SF Pro Text", -apple-system,
								BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
								Arial, "Noto Sans", sans-serif, "Apple Color Emoji",
								"Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
							font-size: 14px;
							margin-bottom: 0px;
						}
						.jam--thread-header-author-avatar {
							align-self: center;
							height: 40px;
							width: 40px;
							border-radius: 50%;
							-webkit-transition-property: none;
							-moz-transition-property: none;
							-o-transition-property: none;
							transition-property: none;
						}

						.jam--thread-header-author-name {
							align-self: center;
							color: #2a3632;
							font-size: 14px;
							margin-left: 16px;
							font-weight: 500;
							line-height: 20px;
							-webkit-transition-property: none;
							-moz-transition-property: none;
							-o-transition-property: none;
							transition-property: none;
						}
						.jam--thread-header-created-at {
							align-self: center;
							color: #d8d8d8;
							font-size: 14px;
							margin-left: 10px;
							line-height: 20px;
							font-weight: 400;
							-webkit-transition-property: none;
							-moz-transition-property: none;
							-o-transition-property: none;
							transition-property: none;
						}
					`}</style>
				</div>
				<div
					onClick={handleResolveClick}
					className="jam--thread-header-resolve-button jam--button"
				>
					<p>{resolveLoading ? "🤸‍♂️..." : "👍"}</p>
				</div>
				<style>{`
					.jam--thread-header-container {
						display: flex;
						flex-direction: row;
						justify-content: space-between;
						align-items: center;
						width: 100%;
						font-size: 14px;
						box-sizing: border-box;
						padding: 1rem;
						-webkit-transition-property: none;
						-moz-transition-property: none;
						-o-transition-property: none;
						transition-property: none;
					}

					.jam--thread-header-resolve-button {
						align-content: flex-end;
						padding: 10px 24px;
						border: 1px solid #47b38f;
						box-sizing: border-box;
						border-radius: 124px;
						display: flex;
						align-items: center;
						justify-content: center;
						height: 40px;
						background: rgba(115, 229, 191, 0.15);
						color: #47b38f;
						font-weight: 500;
						width: 100px;
						-webkit-transition-property: none;
						-moz-transition-property: none;
						-o-transition-property: none;
						transition-property: none;
					}

					.jam--thread-header-resolve-button p {
						line-height: 20px;
						margin: 0;
						color: #47b38f;
						font-size: 14px;
						font-weight: 500;
						-webkit-transition-property: none;
						-moz-transition-property: none;
						-o-transition-property: none;
						transition-property: none;
					}

					.jam--thread-header-resolve-button:hover {
						cursor: pointer;
						-webkit-transition-property: none;
						-moz-transition-property: none;
						-o-transition-property: none;
						transition-property: none;
					}
				`}</style>
			</div>
			{thread.comments.map((comment, index) => {
				let rawMessageBlocks
				try {
					rawMessageBlocks = JSON.parse(comment.message)
				} catch (e) {
					rawMessageBlocks = false
				}
				if (index === 0) {
					let hasembeddedVideo = false
					let embedVideoSrc = getEmbeddableVideo(rawMessageBlocks)

					if (embedVideoSrc !== null) {
						hasembeddedVideo = true
					}

					return (
						<div key={comment._id}>
							<div
								className={
									thread.comments.length > 1
										? "jam--thread-message first-comment with-padding"
										: "jam--thread-message first-comment with-padding"
								}
							>
								<JamEditorDisplayMode
									rawMessageBlocks={rawMessageBlocks}
									img={props.thread.screenshot}
									systemInfo={props.thread.systemInfo}
									location={props.thread.location}
									// css={this.props.thread.cssInput}
									reactions={comment.reactions}
									JAM_ID={props.JAM_ID}
									threadId={props.thread._id}
									commentId={comment._id}
									onCommentChange={props.onCommentChange}
									socket={props.socket}
								/>
								<style>{`
									.jam--thread-message {
										color: #2a3632;
										display: flex;
										font-size: 14px;
										font-size: 14px;
										padding: 0.5rem 0rem 0.5rem 0rem;
										box-sizing: border-box;
										border-bottom: 1px solid #eaeaea;
									}

									.jam--thread-message.first-comment.with-padding {
										padding: 0rem 1rem 1rem 1rem;
										margin-top: -1rem;
									}

									.jam--thread-message-content {
										text-align: left;
										margin: 0;
										font-size: 14px;
									}
								`}</style>
							</div>
							{hasembeddedVideo && embedVideoSrc && (
								<div className="jam--embed-video-container">
									<iframe
										width="100%"
										height="100%"
										src={embedVideoSrc}
										frameBorder="0"
										allowFullScreen
									></iframe>
									<style>{`
										.jam--embed-video-container {
											padding: 15px;
										}
									`}</style>
								</div>
							)}
						</div>
					)
				} else {
					return (
						<div
							key={comment._id}
							className={`jam--comment-container ${thread._id}`}
						>
							<div className="jam--comment-header">
								<img
									className="jam--comment-avatar"
									src={comment.author.avatar}
								/>
								<div className="jam--comment-author">{comment.author.name}</div>
								<div className="jam--comment-time">
									{humanReadableTimeFrom(comment._id)}
								</div>
							</div>
							<JamEditorDisplayMode
								rawMessageBlocks={rawMessageBlocks}
								reactions={comment.reactions}
								JAM_ID={props.JAM_ID}
								threadId={thread._id}
								commentId={comment._id}
								onCommentChange={props.onCommentChange}
								socket={props.socket}
							/>
							<style>{`
								.jam--comment-container {
									display: flex;
									flex-direction: column;
									width: 100%;
									font-size: 14px;
									z-index: 1000000;
									border-bottom: 1px solid #eaeaea;
									padding: initial;
									padding: 1rem;
									box-sizing: border-box;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-header {
									align-content: center;
									display: flex;
									flex-direction: row;
									font-size: 14px;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-avatar {
									align-content: flex-start;
									border-radius: 50%;
									height: 40px;
									width: 40px;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-author {
									align-self: center;
									color: #2a3632;
									font-size: 14px;
									margin-left: 16px;
									font-weight: 500;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-time {
									align-self: center;
									color: #d8d8d8;
									font-size: 14px;
									margin-left: 15px;
									text-align: left !important;
									font-weight: 400;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-message {
									color: #2a3632;
									text-align: left !important;
										{
										/*padding: 1.5rem 0 0.5rem 0;*/
									}
									line-height: 20px;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-message p {
									margin-top: 1rem;
									margin-bottom: 1rem;
									font-style: normal;
									font-weight: normal;
									font-size: 14px;
									color: #2a3632;
									text-align: left !important;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}

								.jam--comment-message .DraftEditor-root {
									padding: 0 !important;
									font-style: normal;
									font-weight: normal;
									line-height: 20px;
									-webkit-transition-property: none;
									-moz-transition-property: none;
									-o-transition-property: none;
									transition-property: none;
								}
							`}</style>
						</div>
					)
				}
			})}
			<JamThreadEditor
				JAM_ID={props.JAM_ID}
				mentions={props.mentions}
				thread={thread}
				onThreadChange={props.onThreadChange}
				loading={props.loading}
				onLoadingChange={props.onLoadingChange}
				socket={props.socket}
			/>
			<style>{`
				.jam--thread-container {
					text-align: left;
					width: 420px;
					max-width: 95%;
					overflow-y: auto;
					background: #fff;
					box-sizing: border-box;
					display: flex;
					flex-direction: column;
					justify-content: space-evenly;
					border-radius: 8px;
					box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
					border: 1px solid #f7f7f7;
					font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont,
						"Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans",
						sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
						"Segoe UI Symbol", "Noto Color Emoji";
					position: ${props.detachedThread ? "fixed" : "absolute"};
					font-size: 14px;
					z-index: ${threadOpen ? 1000001 : 1000000};
					-webkit-transition-property: none;
					-moz-transition-property: none;
					-o-transition-property: none;
					transition-property: none;
					${props.detachedThread ? "right: 0; margin: auto;" : ""}
				}

				@media screen and (max-width: 450px) {
					.jam--thread-container {
						width: 365px;
						margin-left: auto;
						margin-right: auto;
						left: 0 !important;
						right: 0;
						-webkit-transition-property: none;
						-moz-transition-property: none;
						-o-transition-property: none;
						transition-property: none;
					}
				}

				.jam--thread-header-drag {
					width: 15px;
					height: 15px;
					opacity: 0.8;
					background: #fbfbfb;
					margin-right: 8px;
					cursor: pointer;
					border-radius: 2px;
					-webkit-transition-property: none;
					-moz-transition-property: none;
					-o-transition-property: none;
					transition-property: none;
				}

				.jam--thread-header-right-side {
					display: flex;
					flex-direction: row;
					align-items: center;
					-webkit-transition-property: none;
					-moz-transition-property: none;
					-o-transition-property: none;
					transition-property: none;
				}
			`}</style>
		</div>
	)
}
